import { useState } from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  CreatorsApi,
  type CreatorListParams,
  type CreatorRow,
  type CreatorSortField,
  type SortDir,
} from "../api/creators";
import {
  buildCsv,
  buildXlsx,
  exportFilename,
  triggerDownload,
} from "../utils/exportCreators";
import logger from "../analytics/logger";

const PAGE_SIZE = 200;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  params: URLSearchParams;
};

function paramsToListParams(p: URLSearchParams): Omit<CreatorListParams, "page" | "page_size"> {
  const verifiedRaw = p.get("verified");
  const verified: "with" | "without" | undefined =
    verifiedRaw === "with" ? "with" : verifiedRaw === "without" ? "without" : undefined;
  return {
    status: p.get("status") ?? undefined,
    origin: p.get("origin") ?? undefined,
    verified,
    created_after: p.get("created_after") ?? undefined,
    created_before: p.get("created_before") ?? undefined,
    expr: p.get("expr") ?? undefined,
    sort: (p.get("sort") as CreatorSortField | null) ?? undefined,
    dir: (p.get("dir") as SortDir | null) ?? undefined,
  };
}

export default function ExportCreatorsModal({ isOpen, onClose, params }: Props) {
  const [count, setCount] = useState<number>(100);
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [format, setFormat] = useState<"csv" | "xlsx">("csv");
  const [exporting, setExporting] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ fetched: number; total: number } | null>(null);
  const toast = useToast();

  const handleClose = () => {
    if (exporting) return;
    setProgress(null);
    onClose();
  };

  const handleExport = async () => {
    setExporting(true);
    setProgress(null);
    const baseParams = paramsToListParams(params);
    const target = allChecked ? Infinity : Math.max(1, count);
    const out: CreatorRow[] = [];
    try {
      for (let page = 1; out.length < target; page++) {
        const resp = await CreatorsApi.list({
          ...baseParams,
          page,
          page_size: PAGE_SIZE,
        });
        out.push(...resp.creators);
        setProgress({ fetched: out.length, total: resp.total });
        if (resp.creators.length < PAGE_SIZE) break;
        if (out.length >= resp.total) break;
      }
      const rows = allChecked ? out : out.slice(0, count);
      const blob = format === "csv" ? buildCsv(rows) : await buildXlsx(rows);
      triggerDownload(blob, exportFilename(format));
      toast({
        status: "success",
        title: `Exported ${rows.length} creator${rows.length === 1 ? "" : "s"}`,
        duration: 3000,
      });
      setExporting(false);
      setProgress(null);
      onClose();
    } catch (e) {
      logger.error("creators_export_failed", e);
      toast({ status: "error", title: "Export failed", duration: 5000 });
      setExporting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} closeOnOverlayClick={!exporting}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Export creators</ModalHeader>
        <ModalCloseButton isDisabled={exporting} />
        <ModalBody>
          <Stack spacing={5}>
            <FormControl>
              <FormLabel>How many records?</FormLabel>
              <HStack spacing={4}>
                <NumberInput
                  value={count}
                  min={1}
                  max={1000000}
                  isDisabled={allChecked || exporting}
                  onChange={(_, n) => setCount(Number.isFinite(n) ? n : 1)}
                  maxW="160px"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Checkbox
                  isChecked={allChecked}
                  isDisabled={exporting}
                  onChange={(e) => setAllChecked(e.target.checked)}
                >
                  All
                </Checkbox>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>Format</FormLabel>
              <RadioGroup
                value={format}
                onChange={(v) => setFormat(v as "csv" | "xlsx")}
              >
                <HStack spacing={6}>
                  <Radio value="csv" isDisabled={exporting}>
                    CSV
                  </Radio>
                  <Radio value="xlsx" isDisabled={exporting}>
                    XLSX
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            {exporting && progress && (
              <Text fontSize="sm" color="gray.600">
                Fetched {progress.fetched.toLocaleString()} of{" "}
                {progress.total.toLocaleString()}…
              </Text>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={exporting}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleExport}
            isLoading={exporting}
            loadingText="Exporting"
          >
            Export
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
